import { apiClient } from "@/lib/api/http"

// ── Types ────────────────────────────────────────────────────────────────────────
export interface ProductImage {
  id: string
  productId: string
  url: string
  path?: string
  caption?: string | null
  order?: number
  createdAt: string
}

export interface Variant {
  id: string
  productId: string
  sku: string
  name?: string
  description?: string
  tags?: string[]
  hsnCode?: string
  price?: number
  currencyCode?: string
  stockQuantity?: number
  unit?: string
  measureUnit?: string
  createdAt: string
  updatedAt: string
}

export interface Product {
  id: string
  companyId: string
  productName: string
  description: string
  tags: string[]
  productCode: string
  productCategory: string
  productSubCategory: string
  createdAt: string
  updatedAt: string
  variants?: Variant[]
  images?: ProductImage[]
  categoryId?: string
  subCategoryId?: string
  company?: {
    id?: string
    name?: string
  }
  priceRange?: {
    min?: number | null
    max?: number | null
    currencyCode?: string | null
  }
}

export interface CompanyProductsResponse {
  id: string
  name: string
  products: Product[]
}

export interface CreateProductPayload {
  companyId: string
  productName: string
  description?: string
  tags?: string[]
  productCode?: string
  productCategory?: string
  productSubCategory?: string
}

export type UpdateProductPayload = Partial<CreateProductPayload>

export interface AddVariantPayload {
  sku: string
  name?: string
  description?: string
  tags?: string[]
  hsnCode?: string
  price?: number
  currencyCode?: string
  stockQuantity?: number
  measureUnit?: string
}

export type UpdateVariantPayload = Partial<AddVariantPayload>

interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

// ── GET /company/{id}/products ────────────────────────────────────────────────
export async function getCompanyProducts(
  companyId: string,
  token: string
): Promise<CompanyProductsResponse> {
  const { data: envelope } = await apiClient.get<Envelope<CompanyProductsResponse>>(
    `/company/${companyId}/products`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── GET /product/{id} ─────────────────────────────────────────────────────────
export async function getProductById(
  productId: string,
  token?: string
): Promise<Product> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<Product>>(
    `/product/${productId}`,
    { headers }
  )
  return envelope.data
}

// ── POST /product ─────────────────────────────────────────────────────────────
export async function createProduct(
  payload: CreateProductPayload,
  token: string
): Promise<Product> {
  const { data: envelope } = await apiClient.post<Envelope<Product>>(
    "/product",
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── POST /product/{id}/add-variant ────────────────────────────────────────────
export async function addProductVariant(
  productId: string,
  payload: AddVariantPayload,
  token: string
): Promise<Variant> {
  const { data: envelope } = await apiClient.post<Envelope<Variant>>(
    `/product/${productId}/add-variant`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── PATCH /product/{id} ───────────────────────────────────────────────────────
export async function updateProduct(
  productId: string,
  payload: UpdateProductPayload,
  token: string
): Promise<Product> {
  const { data: envelope } = await apiClient.patch<Envelope<Product>>(
    `/product/${productId}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── PATCH /product/variants/{variantId} ──────────────────────────────────────
export async function updateProductVariant(
  variantId: string,
  payload: UpdateVariantPayload,
  token: string
): Promise<Variant> {
  const { data: envelope } = await apiClient.patch<Envelope<Variant>>(
    `/product/variant/${variantId}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── GET /product/variant/{variantId} ─────────────────────────────────────────
export async function getProductVariantById(
  variantId: string,
  token?: string
): Promise<any> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<any>(
    `/product/variant/${variantId}`,
    { headers }
  )
  return envelope.data
}

// ── GET /product/categories ──────────────────────────────────────────────────
export interface ProductCategoryWithSubs {
  name: string
  subCategories: string[]
}

export interface ProductCategoryItem {
  id: string
  name: string
  slug: string
  description?: string | null
  imageUrl?: string | null
  iconUrl?: string | null
  order?: number
  isActive?: boolean
  subCategories?: any[]
}

export async function getProductCategoryItems(token?: string): Promise<ProductCategoryItem[]> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<ProductCategoryItem[]>>(
    "/product/categories",
    { headers }
  )
  return Array.isArray(envelope.data) ? envelope.data : []
}

export async function getProductCategoriesWithSubCategories(token?: string): Promise<ProductCategoryWithSubs[]> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<ProductCategoryWithSubs[]>>(
    "/product/categories",
    { headers }
  )
  return Array.isArray(envelope.data) ? envelope.data : []
}

export async function getProductCategories(token?: string): Promise<string[]> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<any>>(
    "/product/categories",
    { headers }
  )
  if (Array.isArray(envelope.data)) {
    return envelope.data.map((item: any) => item.name || item)
  }
  if (envelope.data && Array.isArray(envelope.data.categoryList)) {
    return envelope.data.categoryList
  }
  return []
}

// ── GET /product/categories/{category}/subcategories ──────────────────────────
export async function getProductSubcategories(category: string, token?: string): Promise<string[]> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<{ subCategoryList: string[] }>>(
    `/product/categories/${encodeURIComponent(category)}/subcategories`,
    { headers }
  )
  return envelope.data.subCategoryList
}

// ── GET /product/category/{category} ──────────────────────────────────────────
export async function getProductsByCategory(category: string, token?: string): Promise<Product[]> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<Product[]>>(
    `/product/category/${encodeURIComponent(category)}`,
    { headers }
  )
  return envelope.data
}

// ── GET /product/subcategory/{subCategory} ────────────────────────────────────
export async function getProductsBySubcategory(subCategory: string, token?: string): Promise<Product[]> {
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`

  const { data: envelope } = await apiClient.get<Envelope<Product[]>>(
    `/product/subcategory/${encodeURIComponent(subCategory)}`,
    { headers }
  )
  return envelope.data
}

// ── GET /product/variants ────────────────────────────────────────────────────
export async function getProductVariants(token: string): Promise<any[]> {
  const { data: envelope } = await apiClient.get<any>(
    "/product/variants",
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data || []
}

// ── POST /product/{id}/images ────────────────────────────────────────────────
export async function uploadProductImage(
  productId: string,
  file: File,
  caption?: string,
  order?: number | string,
  token?: string
): Promise<any> {
  const formData = new FormData()
  formData.append("file", file)
  if (caption) formData.append("caption", caption)
  if (order !== undefined && order !== "") formData.append("order", String(order))

  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/product/${productId}/images`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    }
  )
  return envelope.data
}

// ── DELETE /product/{id}/images/{imageId} ────────────────────────────────────
export async function deleteProductImage(
  productId: string,
  imageId: string,
  token: string
): Promise<any> {
  const { data } = await apiClient.delete<any>(
    `/product/${productId}/images/${imageId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data
}

// ── PATCH /product/{id}/images/{imageId} ─────────────────────────────────────
export async function updateProductImage(
  productId: string,
  imageId: string,
  payload: { caption?: string; order?: number | string },
  token: string
): Promise<any> {
  const body: any = {}
  if (payload.caption !== undefined) body.caption = payload.caption
  if (payload.order !== undefined && payload.order !== "") body.order = Number(payload.order)

  const { data } = await apiClient.patch<any>(
    `/product/${productId}/images/${imageId}`,
    body,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  )
  return data
}